import { useCallback, useEffect, useRef, useState } from 'react'
import {
  CornerDownRight,
  Loader2,
  MessageSquare,
  Pencil,
  Send,
  Trash2,
  X,
} from '@/ui/icons'
import { Button } from '@/ui/design-system'
import { cn } from '@/shared/utils'
import { useAuth } from '@/modules/auth'
import { useTripChat } from '@/modules/trip-chat'
import { useTripOnly } from '@/modules/trips/hooks/useTripData'
import { getUserProfileUrl } from '@/shared/utils/userImages'
import type { TripCrewMessage } from '@/shared/api/types'

interface CrewChatWidgetProps {
  tripId: string
}

type Members = Array<{
  user_id: string | null
  member_type?: string | null
  user?: { user_metadata?: { full_name?: string }; email?: string } | null
  guest_name?: string | null
  user_profile?: { profile_image_path?: string | null } | null
}>

function getMemberName(userId: string, members: Members): string {
  const m = members.find((m) => m.user_id === userId)
  if (!m) return 'Crew member'
  if (m.guest_name) return m.guest_name
  return m.user?.user_metadata?.full_name ?? m.user?.email ?? 'Crew member'
}

function getMemberInitials(userId: string, members: Members): string {
  const m = members.find((m) => m.user_id === userId)
  if (!m) return '?'
  const name = m.guest_name ?? m.user?.user_metadata?.full_name ?? m.user?.email ?? ''
  const parts = String(name).trim().split(/\s+/)
  return ((parts[0]?.charAt(0) ?? '') + (parts[1]?.charAt(0) ?? '')).toUpperCase() || '?'
}

function getMemberAvatarUrl(userId: string, members: Members): string | null {
  const m = members.find((m) => m.user_id === userId)
  if (!m || m.member_type !== 'full_member' || !m.user_profile?.profile_image_path) return null
  return getUserProfileUrl(m.user_profile.profile_image_path)
}

function formatTime(dateString: string): string {
  const d = new Date(dateString)
  const now = new Date()
  const diffMins = Math.floor((now.getTime() - d.getTime()) / 60000)
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  return `${Math.floor(diffHours / 24)}d ago`
}

function MemberAvatar({
  userId,
  members,
  size = 'md',
}: {
  userId: string
  members: Members
  size?: 'sm' | 'md'
}) {
  const avatarUrl = getMemberAvatarUrl(userId, members)
  const initials = getMemberInitials(userId, members)
  const dim = size === 'sm' ? 'h-5 w-5 text-[9px]' : 'h-7 w-7 text-[10px]'
  return avatarUrl ? (
    <img
      src={avatarUrl}
      alt=""
      className={cn(dim, 'rounded-full object-cover flex-shrink-0 border border-earth-200 dark:border-earth-700')}
    />
  ) : (
    <div
      className={cn(
        dim,
        'rounded-full flex-shrink-0 flex items-center justify-center font-semibold',
        'bg-adventure-100 text-adventure-800 dark:bg-adventure-900/40 dark:text-adventure-300',
        'border border-earth-200 dark:border-earth-700'
      )}
    >
      {initials}
    </div>
  )
}

/** Small stacked avatar used in the pill trigger */
function PillAvatar({ userId, members }: { userId: string; members: Members }) {
  const avatarUrl = getMemberAvatarUrl(userId, members)
  const initials = getMemberInitials(userId, members)
  return avatarUrl ? (
    <img
      src={avatarUrl}
      alt=""
      className="h-7 w-7 rounded-full object-cover border-2 border-card dark:border-card"
    />
  ) : (
    <div className="h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-semibold bg-adventure-100 text-adventure-800 dark:bg-adventure-900/50 dark:text-adventure-300 border-2 border-card dark:border-card">
      {initials}
    </div>
  )
}

function MessageRow({
  message,
  isOwn,
  members,
  onReply,
  onEdit,
  onDelete,
}: {
  message: TripCrewMessage
  isOwn: boolean
  members: Members
  onReply: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  const authorName = getMemberName(message.author_id, members)
  const isReply = Boolean(message.parent_id)
  return (
    <div className="group flex items-start gap-2">
      <MemberAvatar userId={message.author_id} members={members} size={isReply ? 'sm' : 'md'} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-1.5 mb-0.5">
          <span className="text-xs font-semibold text-earth-800 dark:text-earth-200 truncate max-w-[120px]">
            {authorName}
          </span>
          <span className="text-xs text-earth-400 dark:text-earth-500 flex-shrink-0">
            {formatTime(message.created_at)}
            {message.edited_at && <span className="ml-1 italic">(edited)</span>}
          </span>
        </div>
        <div className="flex items-start gap-1">
          <p className="flex-1 text-sm text-earth-900 dark:text-foreground break-words whitespace-pre-wrap leading-snug">
            {message.body}
          </p>
          <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 flex-shrink-0 transition-opacity">
            {!isReply && (
              <Button variant="ghost" size="icon" className="h-5 w-5" onClick={onReply} aria-label="Reply">
                <CornerDownRight className="h-3 w-3" />
              </Button>
            )}
            {isOwn && (
              <>
                <Button variant="ghost" size="icon" className="h-5 w-5" onClick={onEdit} aria-label="Edit">
                  <Pencil className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 text-destructive"
                  onClick={onDelete}
                  aria-label="Delete"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const MAX_PILL_AVATARS = 3

export function CrewChatWidget({ tripId }: CrewChatWidgetProps) {
  const { user } = useAuth()
  const { members } = useTripOnly(tripId)

  const {
    messages,
    isLoading,
    error,
    typingUserIds,
    readStates,
    sendMessage,
    updateMessage,
    deleteMessage,
    broadcastTyping,
  } = useTripChat(tripId)

  const [isOpen, setIsOpen] = useState(false)
  const [input, setInput] = useState('')
  const [replyingTo, setReplyingTo] = useState<TripCrewMessage | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editInput, setEditInput] = useState('')
  const [isSending, setIsSending] = useState(false)

  const listRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const typingDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!isOpen) return
    const el = listRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [messages.length, isOpen])

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen])

  const unreadCount = (() => {
    if (!user) return 0
    const myState = readStates.find((s) => s.user_id === user.id)
    if (!myState) return messages.filter((m) => m.author_id !== user.id).length
    const lastRead = new Date(myState.last_read_at).getTime()
    return messages.filter(
      (m) => m.author_id !== user.id && new Date(m.created_at).getTime() > lastRead
    ).length
  })()

  const handleInputChange = useCallback(
    (value: string) => {
      setInput(value)
      if (typingDebounceRef.current) clearTimeout(typingDebounceRef.current)
      broadcastTyping(value.length > 0)
      if (value.length === 0) broadcastTyping(false)
    },
    [broadcastTyping]
  )

  const handleSend = useCallback(async () => {
    const body = input.trim()
    if (!body || isSending) return
    setIsSending(true)
    try {
      await sendMessage(body, replyingTo?.id)
      setInput('')
      setReplyingTo(null)
      broadcastTyping(false)
      inputRef.current?.focus()
    } finally {
      setIsSending(false)
    }
  }, [input, isSending, sendMessage, replyingTo, broadcastTyping])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
      if (e.key === 'Escape') {
        setReplyingTo(null)
        setInput('')
      }
    },
    [handleSend]
  )

  const handleEditSave = useCallback(
    async (messageId: string) => {
      const body = editInput.trim()
      if (!body) return
      await updateMessage(messageId, body)
      setEditingId(null)
      setEditInput('')
    },
    [editInput, updateMessage]
  )

  const typingNames = typingUserIds.map((id) => getMemberName(id, members)).join(', ')

  const rootMessages = messages.filter((m) => !m.parent_id)
  const repliesByParent = messages.reduce<Record<string, TripCrewMessage[]>>((acc, m) => {
    if (m.parent_id) acc[m.parent_id] = [...(acc[m.parent_id] ?? []), m]
    return acc
  }, {})

  // Members with a user_id for the pill avatars
  const membersWithId = members.filter((m) => m.user_id)
  const visiblePillMembers = membersWithId.slice(0, MAX_PILL_AVATARS)
  const pillOverflow = membersWithId.length - MAX_PILL_AVATARS

  return (
    <div className="fixed bottom-20 right-4 md:bottom-4 z-[50] flex flex-col items-end gap-2">
      {/* Expanded chat panel */}
      {isOpen && (
        <div
          className={cn(
            'w-[320px] flex flex-col rounded-2xl overflow-hidden',
            'border border-earth-200 dark:border-earth-700/60',
            'bg-background shadow-2xl shadow-earth-900/25 dark:shadow-earth-950/50'
          )}
          style={{ height: '420px' }}
        >
          {/* Header */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-earth-200 dark:border-earth-700/60 bg-card flex-shrink-0">
            <MessageSquare className="h-4 w-4 text-adventure-500 flex-shrink-0" />
            <span className="flex-1 min-w-0 text-sm font-semibold text-foreground">
              Messages
            </span>
            {unreadCount > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-semibold text-white">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 flex-shrink-0 text-muted-foreground hover:text-foreground"
              onClick={() => setIsOpen(false)}
              aria-label="Minimize chat"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>

          {/* Message list */}
          <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 min-h-0">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-5 w-5 animate-spin text-earth-400" />
              </div>
            ) : rootMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-2">
                <MessageSquare className="h-8 w-8 text-earth-300 dark:text-earth-600" />
                <p className="text-sm text-earth-400 dark:text-muted-foreground text-center">
                  No messages yet.
                  <br />Say hi to the crew!
                </p>
              </div>
            ) : (
              rootMessages.map((root) => (
                <div key={root.id} className="space-y-1.5">
                  {editingId === root.id ? (
                    <div className="flex gap-1 items-center">
                      <input
                        className="flex-1 text-sm rounded-lg border border-earth-300 dark:border-earth-600 bg-background px-2 py-1 focus:outline-none focus:ring-1 focus:ring-adventure-500"
                        value={editInput}
                        onChange={(e) => setEditInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleEditSave(root.id)
                          if (e.key === 'Escape') { setEditingId(null); setEditInput('') }
                        }}
                        autoFocus
                      />
                      <Button size="sm" className="h-7 px-2 text-xs" onClick={() => handleEditSave(root.id)}>Save</Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => { setEditingId(null); setEditInput('') }}>✕</Button>
                    </div>
                  ) : (
                    <MessageRow
                      message={root}
                      isOwn={root.author_id === user?.id}
                      members={members}
                      onReply={() => { setReplyingTo(root); setTimeout(() => inputRef.current?.focus(), 50) }}
                      onEdit={() => { setEditingId(root.id); setEditInput(root.body) }}
                      onDelete={() => deleteMessage(root.id)}
                    />
                  )}
                  {(repliesByParent[root.id] ?? []).length > 0 && (
                    <div className="ml-4 pl-3 border-l-2 border-earth-200 dark:border-earth-700 space-y-1.5">
                      {(repliesByParent[root.id] ?? []).map((reply) =>
                        editingId === reply.id ? (
                          <div key={reply.id} className="flex gap-1 items-center">
                            <input
                              className="flex-1 text-sm rounded-lg border border-earth-300 dark:border-earth-600 bg-background px-2 py-1 focus:outline-none focus:ring-1 focus:ring-adventure-500"
                              value={editInput}
                              onChange={(e) => setEditInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleEditSave(reply.id)
                                if (e.key === 'Escape') { setEditingId(null); setEditInput('') }
                              }}
                              autoFocus
                            />
                            <Button size="sm" className="h-7 px-2 text-xs" onClick={() => handleEditSave(reply.id)}>Save</Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => { setEditingId(null); setEditInput('') }}>✕</Button>
                          </div>
                        ) : (
                          <MessageRow
                            key={reply.id}
                            message={reply}
                            isOwn={reply.author_id === user?.id}
                            members={members}
                            onReply={() => { setReplyingTo(root); setTimeout(() => inputRef.current?.focus(), 50) }}
                            onEdit={() => { setEditingId(reply.id); setEditInput(reply.body) }}
                            onDelete={() => deleteMessage(reply.id)}
                          />
                        )
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Typing indicator */}
          {typingNames && (
            <p className="px-3 text-xs text-earth-400 dark:text-earth-500 italic flex-shrink-0 h-4">
              {typingNames} {typingUserIds.length === 1 ? 'is' : 'are'} typing…
            </p>
          )}

          {/* Reply banner */}
          {replyingTo && (
            <div className="flex items-center gap-2 mx-3 mb-1 px-2 py-1 bg-earth-100 dark:bg-earth-800/60 rounded-lg text-xs text-earth-700 dark:text-earth-300 flex-shrink-0">
              <CornerDownRight className="h-3 w-3 flex-shrink-0" />
              <span className="flex-1 truncate">
                Replying to <span className="font-semibold">{getMemberName(replyingTo.author_id, members)}</span>
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-4 w-4 flex-shrink-0"
                onClick={() => setReplyingTo(null)}
                aria-label="Cancel reply"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}

          {/* Error */}
          {error && <p className="px-3 text-xs text-destructive flex-shrink-0">{error}</p>}

          {/* Compose */}
          {user && (
            <div className="flex items-center gap-2 px-3 py-2.5 border-t border-earth-200 dark:border-earth-700/60 flex-shrink-0 bg-card/50">
              <input
                ref={inputRef}
                className="flex-1 min-w-0 text-sm rounded-xl border border-earth-300 dark:border-earth-600 bg-background px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-adventure-500 dark:focus:ring-adventure-400 placeholder:text-earth-400"
                placeholder={replyingTo ? 'Write a reply…' : 'Message the crew…'}
                value={input}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={handleKeyDown}
                maxLength={2000}
                disabled={isSending}
              />
              <Button
                size="icon"
                onClick={handleSend}
                disabled={!input.trim() || isSending}
                aria-label="Send"
                className="flex-shrink-0 h-8 w-8 rounded-xl"
              >
                {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Pill toggle button */}
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={isOpen ? 'Minimize crew chat' : 'Open crew chat'}
        className={cn(
          'flex items-center gap-2.5 pl-3.5 pr-4 py-2 rounded-full',
          'bg-card border border-earth-200 dark:border-earth-700/60',
          'shadow-lg shadow-earth-900/15 dark:shadow-earth-950/40',
          'hover:bg-earth-50 dark:hover:bg-earth-800/80 transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-adventure-400'
        )}
      >
        {/* Icon with unread badge */}
        <div className="relative flex-shrink-0">
          <MessageSquare className="h-5 w-5 text-adventure-500" />
          {!isOpen && unreadCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-semibold text-white leading-none">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </div>

        {/* Label */}
        <span className="text-sm font-semibold text-foreground whitespace-nowrap">
          Messages
        </span>

        {/* Stacked crew avatars */}
        {visiblePillMembers.length > 0 && (
          <div className="flex items-center -space-x-2 flex-shrink-0">
            {visiblePillMembers.map((m) => (
              <PillAvatar key={m.user_id!} userId={m.user_id!} members={members} />
            ))}
            {pillOverflow > 0 && (
              <div className="h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-semibold bg-earth-100 dark:bg-earth-700 text-earth-600 dark:text-earth-300 border-2 border-card dark:border-card">
                ···
              </div>
            )}
          </div>
        )}
      </button>
    </div>
  )
}
